import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import { SpinnerIcon } from '@/components/icons';
import { cn } from '@/lib/utils/cn';

import {
  BUTTON_BASE,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  type ButtonSize,
  type ButtonVariant,
} from './button-styles';

interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Libellé affiché à côté de l'indicateur pendant le chargement. */
  loadingLabel?: string;
  fullWidth?: boolean;
  /**
   * Icône épinglée au bord droit du bouton.
   *
   * Elle est positionnée en absolu, et non placée à la suite du libellé : le
   * texte reste ainsi optiquement centré dans le bouton, quelle que soit sa
   * longueur. Placée dans le flux, elle décalerait le libellé vers la gauche.
   */
  trailingIcon?: ReactNode;
}

export const Button = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  loadingLabel = 'Chargement…',
  fullWidth = false,
  trailingIcon,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) => (
  <button
    // `type` par défaut à "button" : sans cela, un bouton dans un formulaire
    // le soumet implicitement — source classique de bugs. Les boutons de
    // soumission passent `type="submit"` explicitement.
    type="button"
    disabled={disabled || loading}
    aria-busy={loading}
    className={cn(
      // `group` : permet à `trailingIcon` de réagir au survol du bouton entier,
      // et non du seul espace qu'il occupe.
      BUTTON_BASE,
      loading
        ? 'cursor-wait'
        : 'disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none',
      BUTTON_VARIANTS[variant],
      BUTTON_SIZES[size],
      // Réserve la même place des deux côtés : le texte reste centré sans
      // chevaucher l'icône, même sur un bouton qui n'occupe pas toute la ligne.
      trailingIcon && (size === 'sm' ? 'px-11' : size === 'md' ? 'px-12' : 'px-14'),
      fullWidth && 'w-full',
      className,
    )}
    {...rest}
  >
    {/* Les deux états partagent une cellule : la largeur reste stable même
        lorsque le libellé de chargement est plus long que celui de l'action. */}
    <span className="inline-grid min-w-0 items-center justify-items-center">
      <span
        aria-hidden={loading}
        className={cn(
          'col-start-1 row-start-1 inline-flex items-center justify-center gap-2',
          loading && 'invisible',
        )}
      >
        {children}
      </span>
      <span
        aria-hidden={!loading}
        className={cn(
          'col-start-1 row-start-1 inline-flex items-center justify-center gap-2.5',
          !loading && 'invisible',
        )}
      >
        <span aria-hidden className="grid size-[18px] shrink-0 place-items-center">
          {loading ? <SpinnerIcon className="text-lg motion-reduce:animate-none" /> : null}
        </span>
        {loadingLabel}
      </span>
    </span>

    {trailingIcon ? (
      <span
        aria-hidden
        className={cn(
          // `top-1/2` + translation : centrage vertical explicite. S'en remettre
          // à la position statique d'un élément absolu est fragile dès que le
          // contenu du bouton change de hauteur.
          'pointer-events-none absolute top-1/2 inline-flex -translate-y-1/2 items-center',
          size === 'sm' ? 'right-3' : size === 'md' ? 'right-3.5' : 'right-4',
          loading && 'invisible',
        )}
      >
        {trailingIcon}
      </span>
    ) : null}
  </button>
);
